import { useEffect, useState, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormSpy } from 'react-final-form';

import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import LoadingButton from '@mui/lab/LoadingButton';
import MenuItem from '@mui/material/MenuItem';
import { Select, TextField } from 'mui-rff';

import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import { ACTIONS } from '@components/PropertiesForm/PropertiesForm';
import { selectLoggedInUserEmail } from '@store/auth';

import {
  getCurrencies,
  selectCurrencies,
} from '../../store/payment-options-currencies';
import {
  getPaymentMethods,
  selectMethodList,
} from '../../store/payment-options-methods';
import {
  addProcessingFee,
  updateProcessingFee,
} from '../../store/payment-options-processing-fees';

import {
  PAYMENT_MODE_OPTIONS,
  FEES_TYPE_OPTIONS,
} from '../../payment-options.constants';

const FORM_ID = 'processingFeesForm';

const EMPTY_VALUES = {
  paymentMode: '',
  currency: '',
  methodCode: '',
  processingFees: '',
  feesType: '',
  feesDisplayCode: '',
};

const buildInitialValues = (action, data) => {
  if (action !== ACTIONS.EDIT || !data) return EMPTY_VALUES;

  return {
    paymentMode: data.paymentMode ?? '',
    currency: data.currency ?? '',
    methodCode: data.methodCode ?? '',
    processingFees: data.processingFees ?? '',
    feesType: data.feesType ?? '',
    feesDisplayCode: data.feesDisplayCode ?? '',
  };
};

const buildTitle = (action, lobCode) => {
  if (action === ACTIONS.EDIT) return 'Edit Processing Fee:';
  const suffix = lobCode ? ` to ${lobCode}` : '';
  return `Add Processing Fee${suffix}`;
};

function ProcessingFeesFormModal({
  open,
  action = ACTIONS.ADD,
  data = null,
  lobCode = '',
  closeDialog = () => {},
  submitSuccessCallback = () => {},
}) {
  const dispatch = useDispatch();

  const currencyList = useSelector(selectCurrencies);
  const methodList = useSelector(selectMethodList);
  const loggedInUser = useSelector(selectLoggedInUserEmail);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!open) return;

    setIsFetchingData(true);
    Promise.all([dispatch(getCurrencies()), dispatch(getPaymentMethods())])
      .then(() => setIsFetchingData(false))
      .catch(() => setIsFetchingData(false));
  }, [dispatch, open]);

  const validate = (values) => {
    const errors = {};
    if (!values.paymentMode) errors.paymentMode = 'Required';
    if (!values.currency) errors.currency = 'Required';
    if (!values.methodCode) errors.methodCode = 'Required';
    if (
      values.processingFees === '' ||
      values.processingFees === null ||
      values.processingFees === undefined
    ) {
      errors.processingFees = 'Required';
    } else if (Number.isNaN(Number(values.processingFees))) {
      errors.processingFees = 'Must be a number';
    }
    if (!values.feesType) errors.feesType = 'Required';
    if (!values.feesDisplayCode) errors.feesDisplayCode = 'Required';
    return errors;
  };

  const onSubmit = (values) => {
    setFormErrorMsg('');
    setIsSubmitting(true);

    const basePayload = {
      lobCode: String(lobCode ?? ''),
      currency: String(values.currency ?? ''),
      paymentMode: String(values.paymentMode ?? ''),
      methodCode: String(values.methodCode ?? ''),
      processingFees: Number(values.processingFees),
      feesType: String(values.feesType ?? ''),
      feesDisplayCode: String(values.feesDisplayCode ?? ''),
    };

    const isEdit = action === ACTIONS.EDIT;
    const currentUser = String(loggedInUser ?? '');
    const payload = isEdit
      ? { ...basePayload, modifiedBy: currentUser }
      : {
          ...basePayload,
          createdBy: currentUser,
          modifiedBy: currentUser,
        };

    const thunk =
      isEdit && data?.id
        ? updateProcessingFee({ id: data.id, payload })
        : addProcessingFee(payload);

    dispatch(thunk)
      .then((response) => {
        const body = response?.payload;
        if (body && (body.status === 'SUCCESS' || body.status === 'OK')) {
          submitSuccessCallback();
          closeDialog(true);
        } else {
          setFormErrorMsg(body?.message || 'Request failed. Please try again.');
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const onCancelClick = () => {
    setFormErrorMsg('');
    closeDialog();
  };

  const onSubmitClick = () => {
    document
      .getElementById(FORM_ID)
      .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true });
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      aria-labelledby="processing-fees-form-modal-title">
      <DialogTitle id="processing-fees-form-modal-title">
        {buildTitle(action, lobCode)}
      </DialogTitle>
      <DialogContent style={{ paddingTop: '12px' }}>
        {isFetchingData ? (
          <div style={{ textAlign: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <Form
            onSubmit={onSubmit}
            initialValues={buildInitialValues(action, data)}
            validate={validate}
            render={({ handleSubmit }) => (
              <form
                id={FORM_ID}
                onSubmit={handleSubmit}
                noValidate>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={() => setFormErrorMsg('')}
                />
                <Grid
                  container
                  columnSpacing={2}
                  rowSpacing={4}
                  sx={{ paddingTop: '8px' }}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Select
                      required
                      name="paymentMode"
                      label="Payment Mode"
                      variant="outlined">
                      {PAYMENT_MODE_OPTIONS.map((mode) => (
                        <MenuItem
                          key={mode}
                          value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Select
                      required
                      name="currency"
                      label="Currency"
                      variant="outlined">
                      <MenuItem value="ALL">ALL</MenuItem>
                      {currencyList.map((currency) => (
                        <MenuItem
                          key={currency.id}
                          value={currency.code}>
                          {currency.code}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Select
                      required
                      name="methodCode"
                      label="Method Code"
                      variant="outlined">
                      <MenuItem value="ALL">ALL</MenuItem>
                      {methodList.map((method) => (
                        <MenuItem
                          key={method.methodId}
                          value={method.methodCode}>
                          {`${method.methodName} (${method.methodCode})`}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      name="processingFees"
                      label="Processing Fees"
                      type="number"
                      variant="outlined"
                      inputProps={{ step: '0.01' }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <Select
                      required
                      name="feesType"
                      label="Fees Type"
                      variant="outlined">
                      {FEES_TYPE_OPTIONS.map((type) => (
                        <MenuItem
                          key={type}
                          value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      name="feesDisplayCode"
                      label="Fees Display Code"
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </form>
            )}
          />
        )}
        {formErrorMsg && (
          <Alert
            sx={{ marginTop: '12px' }}
            onClose={() => setFormErrorMsg('')}
            severity="error">
            {formErrorMsg}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={isSubmitting}
          onClick={onCancelClick}>
          Cancel
        </Button>
        <ProdConfirmationDialog
          openDialog={openDialog}
          yesHandler={onSubmitClick}
        />
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          loadingPosition="start"
          startIcon={action === ACTIONS.EDIT ? <SaveIcon /> : <AddIcon />}
          onClick={(e) =>
            process.env.REACT_APP_ENV !== 'production'
              ? onSubmitClick(e)
              : handlePreSubmit(e)
          }>
          {action === ACTIONS.EDIT ? 'Save' : 'Submit'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default memo(ProcessingFeesFormModal);
